import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from '@sea-snag/ui';
import { Building2, ChevronsUpDown, LayoutDashboard, Ship, Users, Workflow } from 'lucide-react';
import * as React from 'react';
import { useMemo, useState } from 'react';

import { useAdminCompanySearch } from '../hooks/use-admin';
import { useEnterCompany } from '../hooks/use-enter-company';
import { getRecentAdminCompanies } from '../stores/org-store';
import { NavMain, type NavSection } from './nav-main';
import { NavUser } from './nav-user';
import { SeaSnagLogo } from './sea-snag-logo';

export function AdminSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { isMobile } = useSidebar();
  const enterCompany = useEnterCompany();
  const [adminSearch, setAdminSearch] = useState('');
  const { data: searchResults } = useAdminCompanySearch(adminSearch);
  const recentCompanies = getRecentAdminCompanies();

  const navSections = useMemo<NavSection[]>(
    () => [
      {
        label: 'Overview',
        items: [
          { title: 'Dashboard', url: '/admin/dashboard', icon: LayoutDashboard },
          { title: 'Companies', url: '/admin/companies', icon: Building2 },
          { title: 'Charters', url: '/admin/charters', icon: Ship },
          { title: 'Users', url: '/admin/users', icon: Users },
          { title: 'Sync Jobs', url: '/admin/jobs', icon: Workflow },
        ],
      },
    ],
    []
  );

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu
              onOpenChange={(open) => {
                if (!open) setAdminSearch('');
              }}
            >
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="group/logo data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                >
                  <div className="group flex aspect-square size-8 items-center justify-center rounded-lg text-foreground">
                    <SeaSnagLogo className="size-5" />
                  </div>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-medium">Super Admin</span>
                    <span className="truncate text-xs">Platform</span>
                  </div>
                  <ChevronsUpDown className="ml-auto" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
                align="start"
                side={isMobile ? 'bottom' : 'right'}
                sideOffset={4}
              >
                {/* Search */}
                <div
                  className="px-2 py-1.5"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <Input
                    placeholder="Search companies..."
                    value={adminSearch}
                    onChange={(e) => setAdminSearch(e.target.value)}
                    className="h-8 text-xs"
                  />
                </div>
                {adminSearch.length >= 2 && searchResults && searchResults.length > 0 && (
                  <>
                    {searchResults.map((result) => (
                      <DropdownMenuItem
                        key={result.pid}
                        className="gap-2 p-2"
                        onClick={() => enterCompany(result)}
                      >
                        <div className="flex size-6 items-center justify-center rounded-md border">
                          <SeaSnagLogo className="size-4 shrink-0" color />
                        </div>
                        {result.name}
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                  </>
                )}

                {/* Recent Companies */}
                {recentCompanies.length > 0 && !adminSearch && (
                  <>
                    <DropdownMenuLabel className="text-muted-foreground text-xs">
                      Recent
                    </DropdownMenuLabel>
                    {recentCompanies.map((company) => (
                      <DropdownMenuItem
                        key={company.pid}
                        className="gap-2 p-2"
                        onClick={() => enterCompany(company)}
                      >
                        <div className="flex size-6 items-center justify-center rounded-md border">
                          <SeaSnagLogo className="size-4 shrink-0" color />
                        </div>
                        {company.name}
                      </DropdownMenuItem>
                    ))}
                  </>
                )}

                {/* Empty state */}
                {recentCompanies.length === 0 && !adminSearch && (
                  <div className="px-2 py-4 text-center text-xs text-muted-foreground">
                    Search for a company or enter one from the Companies page
                  </div>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <NavMain sections={navSections} />
      </SidebarContent>
      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
